import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Bell, MessageSquare, Route, AlertTriangle, CheckCheck, Truck } from 'lucide-react';
import { useNotificationStore, AppNotification } from '../stores/notificationStore';

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  return `${diffDay}d ago`;
}

function notificationIcon(type: string) {
  switch (type) {
    case 'DRIVER_MESSAGE':
      return <MessageSquare className="w-5 h-5 text-blue-500" />;
    case 'DISPATCHER_MESSAGE':
      return <MessageSquare className="w-5 h-5 text-indigo-500" />;
    case 'LOAD_STOPS_CHANGED':
      return <Route className="w-5 h-5 text-orange-500" />;
    case 'ETA_DELAY_ALERT':
      return <AlertTriangle className="w-5 h-5 text-red-500" />;
    default:
      return <Bell className="w-5 h-5 text-gray-500" />;
  }
}

function NotificationItem({
  notification,
  onMarkRead,
  onNavigate,
}: {
  notification: AppNotification;
  onMarkRead: (id: string) => void;
  onNavigate: (loadId: string) => void;
}) {
  const loadId = notification.load_id || (notification.data as Record<string, unknown>)?.load_id as string | undefined;
  const hasLoad = !!loadId;
  const isMessage = notification.notification_type === 'DRIVER_MESSAGE' || notification.notification_type === 'DISPATCHER_MESSAGE';

  return (
    <div
      className={`flex gap-3 p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
        !notification.is_read ? 'bg-blue-50/50' : ''
      }`}
      onClick={() => {
        if (!notification.is_read) onMarkRead(notification.id);
        if (hasLoad && loadId) onNavigate(loadId);
      }}
    >
      <div className="flex-shrink-0 mt-0.5">
        {notificationIcon(notification.notification_type)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm ${!notification.is_read ? 'font-semibold' : 'font-medium'} text-gray-900 truncate`}>
            {notification.title}
          </p>
          {!notification.is_read && (
            <span className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-blue-500" />
          )}
        </div>
        <p className="text-sm text-gray-600 line-clamp-2 mt-0.5">{notification.body}</p>
        {hasLoad && (
          <p className="text-xs text-indigo-500 mt-0.5 flex items-center gap-1">
            <Truck className="w-3 h-3" />
            {isMessage
              ? ((notification.data as Record<string, unknown>)?.load_number as string || 'View Load')
              : 'View Load Details'}
          </p>
        )}
        <p className="text-xs text-gray-400 mt-1">
          {notification.sender_name && <span>{notification.sender_name} &middot; </span>}
          {timeAgo(notification.created_at)}
        </p>
      </div>
    </div>
  );
}

export function NotificationPanel() {
  const {
    notifications,
    isPanelOpen,
    isLoading,
    unreadCount,
    closePanel,
    markAllRead,
    markRead,
    navigateToLoad,
  } = useNotificationStore();
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  function handleNotificationNavigate(loadId: string) {
    // The live map (legacy Dashboard content) is mounted inside /today; it
    // consumes the pending navigation on mount or immediately when mounted.
    navigateToLoad(loadId);
    navigate('/today?view=map');
  }

  // Escape closes the panel
  useEffect(() => {
    if (!isPanelOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closePanel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isPanelOpen, closePanel]);

  // Close on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        closePanel();
      }
    }
    if (isPanelOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isPanelOpen, closePanel]);

  if (!isPanelOpen) return null;

  return (
    <div className="fixed inset-0 z-[65] flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20" onClick={closePanel} />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Notifications"
        className="relative w-full max-w-md bg-white shadow-xl flex flex-col animate-slide-in-right"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-bold bg-red-100 text-red-700 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Mark all as read"
              >
                <CheckCheck className="w-4 h-4" />
                Mark all read
              </button>
            )}
            <button
              onClick={closePanel}
              className="p-1 rounded hover:bg-gray-200 transition-colors"
              aria-label="Close notifications"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Notification list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Bell className="w-12 h-12 mb-2" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            notifications.map((n) => (
              <NotificationItem key={n.id} notification={n} onMarkRead={markRead} onNavigate={handleNotificationNavigate} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
